import { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

function pctBar(percent: number) {
  const color =
    percent >= 100 ? 'bg-green-500' : percent >= 70 ? 'bg-blue-500' : percent >= 40 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
      <div className={`${color} h-2 rounded-full`} style={{ width: `${Math.min(100, percent)}%` }} />
    </div>
  );
}

export function BranchObjectives() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [branchId, setBranchId] = useState('');
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingBranch, setEditingBranch] = useState<string | null>(null);
  const [form, setForm] = useState({
    salesAmountTarget: '',
    salesCountTarget: '',
    collectionTarget: '',
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, brs] = await Promise.all([
        apiService.getBranchTargetsDashboard({ year, month, branchId: branchId || undefined }),
        apiService.getBranches(true),
      ]);
      setSnapshots(dash);
      setBranches(brs);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [year, month, branchId]);

  const startEdit = (s: any) => {
    setEditingBranch(s.branchId);
    setForm({
      salesAmountTarget: String(s.salesAmountTarget),
      salesCountTarget: String(s.salesCountTarget),
      collectionTarget: String(s.collectionTarget),
    });
  };

  const saveTarget = async (branchIdToSave: string) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.upsertBranchTarget({
        branchId: branchIdToSave,
        year,
        month,
        salesAmountTarget: parseFloat(form.salesAmountTarget),
        salesCountTarget: parseInt(form.salesCountTarget, 10),
        collectionTarget: parseFloat(form.collectionTarget),
      });
      setEditingBranch(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Objetivos por sucursal</h1>
        <p className="text-sm text-gray-500 mt-1">Ventas y cobranza vs meta — avance diario</p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="mb-6 flex flex-wrap gap-4 items-end bg-white p-4 rounded-lg border border-gray-200">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Año</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="px-3 py-2 border border-gray-300 rounded-md w-24"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Mes</label>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Sucursal</label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md min-w-[200px]"
          >
            <option value="">Todas</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <button onClick={load} className="px-4 py-2 text-sm text-blue-600 hover:underline">Actualizar</button>
      </div>

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : snapshots.length === 0 ? (
        <p className="text-gray-500">Sin datos para el período.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {snapshots.map((s) => (
            <div key={s.branchId} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{s.branchName}</h2>
                  <p className="text-xs text-gray-500 font-mono">{s.branchCode}</p>
                </div>
                {editingBranch === s.branchId ? (
                  <button
                    disabled={saving}
                    onClick={() => saveTarget(s.branchId)}
                    className="text-sm text-green-600 hover:underline disabled:opacity-50"
                  >
                    Guardar
                  </button>
                ) : (
                  <button onClick={() => startEdit(s)} className="text-sm text-blue-600 hover:underline">
                    Editar metas
                  </button>
                )}
              </div>

              {editingBranch === s.branchId && (
                <div className="mb-4 grid grid-cols-3 gap-2 text-sm">
                  <input
                    placeholder="Meta ventas $"
                    value={form.salesAmountTarget}
                    onChange={(e) => setForm({ ...form, salesAmountTarget: e.target.value })}
                    className="px-2 py-1 border rounded"
                  />
                  <input
                    placeholder="Meta cant."
                    value={form.salesCountTarget}
                    onChange={(e) => setForm({ ...form, salesCountTarget: e.target.value })}
                    className="px-2 py-1 border rounded"
                  />
                  <input
                    placeholder="Meta cobranza $"
                    value={form.collectionTarget}
                    onChange={(e) => setForm({ ...form, collectionTarget: e.target.value })}
                    className="px-2 py-1 border rounded"
                  />
                </div>
              )}

              <div className="space-y-4 text-sm">
                <div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas (monto)</span>
                    <span className="font-medium">
                      {s.salesAmountTarget <= 0 ? (
                        <>${s.salesAmountActual.toLocaleString()} · Sin objetivo configurado</>
                      ) : (
                        <>
                          ${s.salesAmountActual.toLocaleString()} / ${s.salesAmountTarget.toLocaleString()}
                          <span className="text-gray-400 ml-1">({s.salesAmountPercent}%)</span>
                        </>
                      )}
                    </span>
                  </div>
                  {s.salesAmountTarget > 0 && pctBar(s.salesAmountPercent)}
                  {s.remainingDays > 0 && s.missingSalesAmount > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Faltan ${s.missingSalesAmount.toLocaleString()} — ${s.dailySalesAmountNeeded.toLocaleString()}/día ({s.remainingDays} días)
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas (cantidad)</span>
                    <span className="font-medium">
                      {s.salesCountTarget <= 0 ? (
                        <>{s.salesCountActual} · Sin objetivo configurado</>
                      ) : (
                        <>
                          {s.salesCountActual} / {s.salesCountTarget}
                          <span className="text-gray-400 ml-1">({s.salesCountPercent}%)</span>
                        </>
                      )}
                    </span>
                  </div>
                  {s.salesCountTarget > 0 && pctBar(s.salesCountPercent)}
                </div>
                <div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cobranza</span>
                    <span className="font-medium">
                      {s.collectionTarget <= 0 ? (
                        <>${s.collectionActual.toLocaleString()} · Sin objetivo configurado</>
                      ) : (
                        <>
                          ${s.collectionActual.toLocaleString()} / ${s.collectionTarget.toLocaleString()}
                          <span className="text-gray-400 ml-1">({s.collectionPercent}%)</span>
                        </>
                      )}
                    </span>
                  </div>
                  {s.collectionTarget > 0 && pctBar(s.collectionPercent)}
                  {s.remainingDays > 0 && s.missingCollection > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Faltan ${s.missingCollection.toLocaleString()} — ${s.dailyCollectionNeeded.toLocaleString()}/día
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
